<template>
  <span class="brand-logo" :class="`brand-logo--${size}`" aria-label="易智造">
    <svg class="brand-logo__mark" viewBox="0 0 96 96" role="img" focusable="false">
      <defs>
        <linearGradient id="yzLogoBase" x1="14" y1="10" x2="84" y2="88" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#f7f4ff" />
          <stop offset="0.33" stop-color="#9367ff" />
          <stop offset="0.62" stop-color="#4f3df6" />
          <stop offset="1" stop-color="#39c7ff" />
        </linearGradient>
        <linearGradient id="yzLogoSurface" x1="14" y1="6" x2="72" y2="90" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#ffffff" stop-opacity="0.68" />
          <stop offset="0.28" stop-color="#ffffff" stop-opacity="0.22" />
          <stop offset="1" stop-color="#ffffff" stop-opacity="0" />
        </linearGradient>
        <linearGradient id="yzLogoBorder" x1="14" y1="8" x2="86" y2="88" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#ffffff" stop-opacity="0.86" />
          <stop offset="0.48" stop-color="#8e79ff" stop-opacity="0.95" />
          <stop offset="1" stop-color="#74ecff" stop-opacity="0.95" />
        </linearGradient>
        <linearGradient id="yzLogoLetters" x1="23" y1="25" x2="75" y2="76" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="#ffffff" stop-opacity="0.98" />
          <stop offset="0.52" stop-color="#f1e8ff" stop-opacity="0.82" />
          <stop offset="1" stop-color="#d8f5ff" stop-opacity="0.9" />
        </linearGradient>
        <radialGradient id="yzLogoVioletGlow" cx="30%" cy="80%" r="58%">
          <stop offset="0" stop-color="#a855f7" stop-opacity="0.78" />
          <stop offset="1" stop-color="#a855f7" stop-opacity="0" />
        </radialGradient>
        <radialGradient id="yzLogoBlueGlow" cx="86%" cy="84%" r="58%">
          <stop offset="0" stop-color="#38bdf8" stop-opacity="0.78" />
          <stop offset="1" stop-color="#38bdf8" stop-opacity="0" />
        </radialGradient>
        <filter id="yzLogoShadow" x="-22%" y="-22%" width="144%" height="144%">
          <feDropShadow dx="0" dy="8" stdDeviation="7" flood-color="#5b4df2" flood-opacity="0.28" />
        </filter>
        <filter id="yzLogoGlass" x="-12%" y="-12%" width="124%" height="124%">
          <feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-color="#ffffff" flood-opacity="0.82" />
          <feDropShadow dx="0" dy="4" stdDeviation="3" flood-color="#20107d" flood-opacity="0.18" />
        </filter>
      </defs>

      <rect x="8" y="8" width="80" height="80" rx="22" fill="url(#yzLogoBase)" filter="url(#yzLogoShadow)" />
      <rect x="8" y="8" width="80" height="80" rx="22" fill="url(#yzLogoVioletGlow)" />
      <rect x="8" y="8" width="80" height="80" rx="22" fill="url(#yzLogoBlueGlow)" />
      <rect x="9.5" y="9.5" width="77" height="77" rx="20.5" fill="url(#yzLogoSurface)" />
      <rect x="9.5" y="9.5" width="77" height="77" rx="20.5" fill="none" stroke="url(#yzLogoBorder)" stroke-width="2.4" />
      <path class="brand-logo__rim brand-logo__rim--top" d="M18 25.5C22 16.5 31 14 42 14h23c8 0 14 2.5 18.5 8" />
      <path class="brand-logo__rim brand-logo__rim--side" d="M84 28v35c0 11-7.5 19.5-19.5 21" />
      <text x="18" y="67" class="brand-logo__letters brand-logo__letters--back">yz</text>
      <text x="18" y="67" class="brand-logo__letters">yz</text>
      <path class="brand-logo__letter-glint" d="M28 33c11-5 26-6 39-2" />
    </svg>
  </span>
</template>

<script setup lang="ts">
withDefaults(defineProps<{
  size?: 'sm' | 'md' | 'lg'
}>(), {
  size: 'md'
})
</script>

<style scoped>
.brand-logo {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: visible;
}

.brand-logo__mark {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
}

.brand-logo__rim,
.brand-logo__letter-glint {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.brand-logo__rim--top {
  stroke: rgba(255, 255, 255, 0.72);
  stroke-width: 2.2;
}

.brand-logo__rim--side {
  stroke: rgba(103, 232, 249, 0.72);
  stroke-width: 2;
}

.brand-logo__letters {
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 58px;
  font-style: italic;
  font-weight: 900;
  letter-spacing: -7px;
  fill: url(#yzLogoLetters);
  stroke: rgba(255, 255, 255, 0.72);
  stroke-width: 1.15px;
  paint-order: stroke fill;
  filter: url(#yzLogoGlass);
}

.brand-logo__letters--back {
  fill: none;
  stroke: rgba(89, 61, 246, 0.34);
  stroke-width: 5.6px;
  filter: none;
}

.brand-logo__letter-glint {
  stroke: rgba(255, 255, 255, 0.5);
  stroke-width: 1.4;
}

.brand-logo--sm {
  width: 32px;
  height: 32px;
}

.brand-logo--md {
  width: 38px;
  height: 38px;
}

.brand-logo--lg {
  width: 42px;
  height: 42px;
}
</style>
